import React, { useState, useEffect, useCallback } from 'react';
import { analyticsAPI, activityAPI } from '../../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { ProfilePhoto } from '../ProfilePhoto';
import { 
  BarChart3, TrendingUp, Users, Award, Target, Activity,
  Zap, Coins, Calendar, ArrowUp, ArrowDown, Info, X, RefreshCw,
  AlertTriangle, CheckCircle2, Search
} from 'lucide-react';
import { toast } from 'sonner';

export function AnalyticsTab({ classId }) {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [period, setPeriod] = useState('30');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [metricDialog, setMetricDialog] = useState({ open: false, metric: null });
  const [progressData, setProgressData] = useState(null);
  const [progressSearch, setProgressSearch] = useState('');
  const [progressFilter, setProgressFilter] = useState('all');

  const loadAnalytics = useCallback(async (showRefreshing = false) => {
    if (showRefreshing) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      let data;
      if (period === 'custom' && customStart && customEnd) {
        const res = await analyticsAPI.getClassAnalytics(classId, 30, customStart, customEnd);
        data = res.data;
      } else {
        const res = await analyticsAPI.getClassAnalytics(classId, parseInt(period));
        data = res.data;
      }
      setAnalytics(data);
      if (showRefreshing) {
        toast.success('Analytics actualizado');
      }
    } catch (error) {
      toast.error('Error cargando analytics');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [classId, period, customStart, customEnd]);

  const handleRefresh = () => {
    loadAnalytics(true);
    loadProgress();
  };

  const loadProgress = useCallback(async () => {
    try {
      const res = await activityAPI.getStudentProgress(classId);
      setProgressData(res.data);
    } catch { /* non-critical */ }
  }, [classId]);

  useEffect(() => {
    loadAnalytics();
    loadProgress();
  }, [loadAnalytics, loadProgress]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!analytics) return null;

  const { daily_stats, engagement, top_activities, achievement_distribution, level_distribution } = analytics;

  // Calculate trend (compare last 7 days to previous 7 days)
  const recentCompletions = daily_stats.slice(-7).reduce((sum, d) => sum + d.completions, 0);
  const previousCompletions = daily_stats.slice(-14, -7).reduce((sum, d) => sum + d.completions, 0);
  const trend = previousCompletions > 0 
    ? Math.round(((recentCompletions - previousCompletions) / previousCompletions) * 100) 
    : 0;

  // Calculate chart max with +20 buffer for better visualization
  const chartData = daily_stats.slice(-14);
  const maxCompletions = Math.max(...chartData.map(d => d.completions), 0);
  const chartMax = maxCompletions + 20;

  // Metric details for dialogs
  const metricDetails = {
    active_students: {
      title: 'Estudiantes Activos',
      icon: Users,
      color: 'green',
      description: 'Número de estudiantes que han completado al menos una actividad en el período seleccionado.',
      details: [
        { label: 'Total Inscritos', value: `${engagement.total_active_students} estudiantes activos` },
        { label: 'Tasa de Engagement', value: `${engagement.engagement_rate}%` },
        { label: 'Cálculo', value: 'Estudiantes con ≥1 actividad completada / Total inscritos × 100' }
      ],
      tips: [
        'Una tasa de engagement superior al 70% es excelente',
        'Considera crear actividades más accesibles si el engagement es bajo',
        'Las actividades recurrentes ayudan a mantener el engagement'
      ]
    },
    avg_activities: {
      title: 'Promedio de Actividades',
      icon: Activity,
      color: 'blue',
      description: 'Cantidad promedio de actividades completadas por cada estudiante inscrito.',
      details: [
        { label: 'Promedio', value: `${engagement.avg_activities_per_student} actividades/estudiante` },
        { label: 'Total Completadas', value: `${daily_stats.reduce((s, d) => s + d.completions, 0)} actividades` }
      ],
      tips: [
        'Un promedio bajo puede indicar actividades muy difíciles',
        'Considera dividir actividades grandes en tareas más pequeñas',
        'Las actividades recurrentes aumentan este promedio'
      ]
    },
    achievements: {
      title: 'Logros Desbloqueados',
      icon: Award,
      color: 'yellow',
      description: 'Número total de logros que han sido desbloqueados por los estudiantes.',
      details: [
        { label: 'Total Logros', value: `${engagement.total_achievements_unlocked} desbloqueados` },
        { label: 'Más Popular', value: achievement_distribution[0]?.name || 'N/A' }
      ],
      tips: [
        'Los logros motivan a los estudiantes a mejorar',
        'Crea logros con diferentes niveles de dificultad',
        'Logros basados en habilidades promueven el desarrollo integral'
      ]
    },
    trend: {
      title: 'Tendencia de Actividad',
      icon: TrendingUp,
      color: 'purple',
      description: 'Comparación de la actividad de los últimos 7 días contra la semana anterior.',
      details: [
        { label: 'Últimos 7 días', value: `${recentCompletions} completaciones` },
        { label: 'Semana anterior', value: `${previousCompletions} completaciones` },
        { label: 'Cambio', value: `${trend > 0 ? '+' : ''}${trend}%` }
      ],
      tips: [
        'Una tendencia positiva indica mayor participación',
        'Tendencias negativas pueden ser normales en períodos de exámenes',
        'Usa esta métrica para ajustar tu estrategia de gamificación'
      ]
    }
  };

  const openMetricDialog = (metricKey) => {
    setMetricDialog({ open: true, metric: metricDetails[metricKey] });
  };

  return (
    <div className="space-y-6" data-testid="analytics-tab">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BarChart3 className="w-6 h-6 text-blue-600" />
          <h2 className="text-xl font-bold">Analytics de Engagement</h2>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            className="gap-2"
            data-testid="refresh-analytics-btn"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Actualizando...' : 'Actualizar'}
          </Button>
          <div className="flex items-center gap-2 flex-wrap">
            {['7', '15', '30', '60'].map((d) => (
              <Button
                key={d}
                size="sm"
                variant={period === d ? 'default' : 'outline'}
                onClick={() => { setPeriod(d); setCustomStart(''); setCustomEnd(''); }}
                className="h-8 px-3 text-xs"
                data-testid={`period-${d}-btn`}
              >
                {d} días
              </Button>
            ))}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  size="sm"
                  variant={period === 'custom' ? 'default' : 'outline'}
                  className="h-8 px-3 text-xs gap-1"
                  data-testid="period-custom-btn"
                >
                  <Calendar className="w-3 h-3" />
                  {period === 'custom' && customStart ? `${customStart} - ${customEnd}` : 'Personalizado'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-3 space-y-3">
                <div className="space-y-2">
                  <Label className="text-xs">Desde</Label>
                  <Input
                    type="date"
                    value={customStart}
                    onChange={(e) => setCustomStart(e.target.value)}
                    className="h-8 text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Hasta</Label>
                  <Input
                    type="date"
                    value={customEnd}
                    onChange={(e) => setCustomEnd(e.target.value)}
                    className="h-8 text-sm"
                  />
                </div>
                <Button
                  size="sm"
                  className="w-full h-8"
                  disabled={!customStart || !customEnd}
                  onClick={() => setPeriod('custom')}
                >
                  Aplicar
                </Button>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </div>

      {/* Key Metrics - Clickable */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card 
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => openMetricDialog('active_students')}
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Estudiantes Activos</p>
                <p className="text-2xl font-bold">{engagement.total_active_students}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-xl">
                <Users className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <div className="mt-2 flex items-center gap-1">
              <Badge variant="outline" className="text-green-600 bg-green-50">
                {engagement.engagement_rate}% engagement
              </Badge>
              <Info className="w-3 h-3 text-slate-400 ml-auto" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => openMetricDialog('avg_activities')}
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Prom. Actividades</p>
                <p className="text-2xl font-bold">{engagement.avg_activities_per_student}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-xl">
                <Activity className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <div className="mt-2 flex items-center">
              <p className="text-xs text-slate-500">por estudiante</p>
              <Info className="w-3 h-3 text-slate-400 ml-auto" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => openMetricDialog('achievements')}
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Logros Desbloqueados</p>
                <p className="text-2xl font-bold">{engagement.total_achievements_unlocked}</p>
              </div>
              <div className="p-3 bg-yellow-100 rounded-xl">
                <Award className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
            <div className="mt-2 flex items-center">
              <Info className="w-3 h-3 text-slate-400 ml-auto" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => openMetricDialog('trend')}
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Tendencia</p>
                <p className="text-2xl font-bold flex items-center gap-1">
                  {trend > 0 ? '+' : ''}{trend}%
                  {trend > 0 ? (
                    <ArrowUp className="w-5 h-5 text-green-500" />
                  ) : trend < 0 ? (
                    <ArrowDown className="w-5 h-5 text-red-500" />
                  ) : null}
                </p>
              </div>
              <div className="p-3 bg-purple-100 rounded-xl">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
            </div>
            <div className="mt-2 flex items-center">
              <p className="text-xs text-slate-500">vs semana anterior</p>
              <Info className="w-3 h-3 text-slate-400 ml-auto" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Activity Chart - Improved */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Actividad Diaria
          </CardTitle>
          <CardDescription>Actividades completadas por día (máx: {chartMax})</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Chart with Y-axis labels */}
          <div className="flex">
            {/* Y-axis */}
            <div className="flex flex-col justify-between h-48 pr-2 text-xs text-slate-500">
              <span>{chartMax}</span>
              <span>{Math.round(chartMax * 0.75)}</span>
              <span>{Math.round(chartMax * 0.5)}</span>
              <span>{Math.round(chartMax * 0.25)}</span>
              <span>0</span>
            </div>
            
            {/* Bars */}
            <div className="flex-1 h-48 flex items-end gap-1 border-l border-b border-slate-200 pl-2">
              {chartData.map((day) => (
                <div key={day.date} className="flex-1 flex flex-col items-center gap-1 h-full justify-end">
                  <div 
                    className="w-full bg-blue-500 rounded-t transition-all hover:bg-blue-600 relative group"
                    style={{ 
                      height: `${(day.completions / chartMax) * 100}%`,
                      minHeight: day.completions > 0 ? '4px' : '0'
                    }}
                  >
                    {/* Tooltip */}
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-slate-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                      {day.completions} actividades
                      <br />
                      {day.coins_awarded} coins
                      <br />
                      {day.xp_awarded} XP
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* X-axis labels */}
          <div className="flex pl-8 mt-1">
            {chartData.map((day) => (
              <div key={day.date} className="flex-1 text-center">
                <span className="text-[9px] text-slate-400">
                  {new Date(day.date).toLocaleDateString('es', { day: 'numeric' })}
                </span>
              </div>
            ))}
          </div>
          
          {/* Legend */}
          <div className="mt-4 flex items-center justify-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-blue-500 rounded" />
              <span className="text-slate-600">Actividades Completadas</span>
            </div>
          </div>
          
          {/* Summary Stats */}
          <div className="mt-6 grid grid-cols-3 gap-4 text-center">
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-500">Total Completadas</p>
              <p className="text-xl font-bold">{daily_stats.reduce((s, d) => s + d.completions, 0)}</p>
            </div>
            <div className="p-3 bg-yellow-50 rounded-lg">
              <p className="text-sm text-slate-500">Coins Otorgados</p>
              <p className="text-xl font-bold text-yellow-600">
                {daily_stats.reduce((s, d) => s + d.coins_awarded, 0).toLocaleString()}
              </p>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-slate-500">XP Otorgado</p>
              <p className="text-xl font-bold text-blue-600">
                {daily_stats.reduce((s, d) => s + d.xp_awarded, 0).toLocaleString()}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Two column layout */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Top Activities */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5" />
              Actividades Más Populares
            </CardTitle>
          </CardHeader>
          <CardContent>
            {top_activities.length === 0 ? (
              <p className="text-center text-slate-500 py-4">Sin datos</p>
            ) : (
              <div className="space-y-3">
                {top_activities.map((activity, index) => (
                  <div key={activity.id} className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-bold">
                      {index + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{activity.title}</p>
                      <p className="text-sm text-slate-500">{activity.completions} completadas</p>
                    </div>
                    <Badge variant="outline" className="flex items-center gap-1">
                      <Coins className="w-3 h-3" />
                      {activity.coin_value}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
            {engagement.most_popular_activity && (
              <div className="mt-4 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg">
                <p className="text-sm text-slate-600">
                  <span className="font-medium">Más popular:</span> {engagement.most_popular_activity}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Level Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5" />
              Distribución por Nivel
            </CardTitle>
          </CardHeader>
          <CardContent>
            {level_distribution.length === 0 ? (
              <p className="text-center text-slate-500 py-4">Sin datos</p>
            ) : (
              <div className="space-y-2">
                {level_distribution.map((level) => (
                  <div key={level.level} className="flex items-center gap-3">
                    <span className="w-16 text-sm font-medium">Nivel {level.level}</span>
                    <div className="flex-1 h-6 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full transition-all"
                        style={{ 
                          width: `${(level.count / Math.max(...level_distribution.map(l => l.count))) * 100}%` 
                        }}
                      />
                    </div>
                    <span className="w-12 text-sm text-slate-600 text-right">{level.count}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Achievements Distribution */}
      {achievement_distribution.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Award className="w-5 h-5" />
              Logros Obtenidos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {achievement_distribution.map((ach) => (
                <div 
                  key={ach.name}
                  className="px-4 py-2 rounded-lg flex items-center gap-2"
                  style={{ backgroundColor: `${ach.color}15` }}
                >
                  <Award className="w-4 h-4" style={{ color: ach.color }} />
                  <span className="font-medium">{ach.name}</span>
                  <Badge style={{ backgroundColor: ach.color, color: 'white' }}>
                    {ach.count}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Student Progress Panel */}
      {progressData && (
        <Card data-testid="student-progress-panel">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Target className="w-5 h-5" />
                  Progreso por Estudiante
                </CardTitle>
                <CardDescription>
                  {progressData.total_activities} actividades totales &middot; Promedio: {progressData.avg_progress}%
                  {progressData.at_risk_count > 0 && (
                    <span className="ml-2 text-red-600 font-medium">
                      <AlertTriangle className="w-3 h-3 inline mr-1" />{progressData.at_risk_count} en riesgo (&lt;30%)
                    </span>
                  )}
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Buscar..."
                    value={progressSearch}
                    onChange={(e) => setProgressSearch(e.target.value)}
                    className="h-8 pl-8 w-44 text-sm"
                    data-testid="progress-search"
                  />
                </div>
                <div className="flex gap-1">
                  {[
                    { key: 'all', label: 'Todos' },
                    { key: 'risk', label: 'En riesgo' },
                    { key: 'good', label: 'Al día' },
                  ].map(f => (
                    <Button
                      key={f.key}
                      size="sm"
                      variant={progressFilter === f.key ? 'default' : 'outline'}
                      onClick={() => setProgressFilter(f.key)}
                      className="h-7 px-2 text-xs"
                    >
                      {f.label}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-[480px] overflow-y-auto">
              {progressData.students
                .filter(s => {
                  if (progressFilter === 'risk') return s.at_risk;
                  if (progressFilter === 'good') return !s.at_risk;
                  return true;
                })
                .filter(s => !progressSearch || s.student_name.toLowerCase().includes(progressSearch.toLowerCase()))
                .map(s => (
                  <div
                    key={s.student_id}
                    className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                      s.at_risk ? 'border-red-200 bg-red-50/50' : 'border-slate-100 hover:bg-slate-50'
                    }`}
                    data-testid={`progress-student-${s.student_id}`}
                  >
                    <ProfilePhoto photoUrl={s.photo_url} name={s.student_name} size={36} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm text-slate-900 truncate">{s.student_name}</p>
                        {s.at_risk && (
                          <Badge className="text-[10px] h-4 px-1.5 bg-red-100 text-red-700 border-red-200">
                            <AlertTriangle className="w-2.5 h-2.5 mr-0.5" />Riesgo
                          </Badge>
                        )}
                        {s.progress_pct >= 80 && (
                          <Badge className="text-[10px] h-4 px-1.5 bg-emerald-100 text-emerald-700 border-emerald-200">
                            <CheckCircle2 className="w-2.5 h-2.5 mr-0.5" />Excelente
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${
                              s.progress_pct >= 80 ? 'bg-emerald-500' :
                              s.progress_pct >= 50 ? 'bg-blue-500' :
                              s.progress_pct >= 30 ? 'bg-amber-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${Math.max(s.progress_pct, 2)}%` }}
                          />
                        </div>
                        <span className="text-xs font-medium text-slate-600 w-10 text-right">{s.progress_pct}%</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-slate-500 flex-shrink-0">
                      <div className="text-center">
                        <p className="font-bold text-slate-700">{s.completed}/{s.total_activities}</p>
                        <p className="text-[10px]">Actividades</p>
                      </div>
                      <div className="text-center">
                        <p className="font-bold text-purple-600">{s.total_xp}</p>
                        <p className="text-[10px]">XP</p>
                      </div>
                      <div className="text-center">
                        <p className="font-bold text-amber-600">{s.total_coins}</p>
                        <p className="text-[10px]">Coins</p>
                      </div>
                      <div className="text-center">
                        <p className="font-bold text-blue-600">Nv.{s.level}</p>
                        <p className="text-[10px]">Nivel</p>
                      </div>
                    </div>
                  </div>
                ))}
              {progressData.students.length === 0 && (
                <p className="text-center text-slate-500 py-8">No hay estudiantes inscritos</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Metric Detail Dialog */}
      <Dialog open={metricDialog.open} onOpenChange={(open) => setMetricDialog({ ...metricDialog, open })}>
        <DialogContent className="max-w-md">
          {metricDialog.metric && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {metricDialog.metric.icon && (
                    <div className={`p-2 bg-${metricDialog.metric.color}-100 rounded-lg`}>
                      <metricDialog.metric.icon className={`w-5 h-5 text-${metricDialog.metric.color}-600`} />
                    </div>
                  )}
                  {metricDialog.metric.title}
                </DialogTitle>
                <DialogDescription>
                  {metricDialog.metric.description}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4">
                {/* Details */}
                <div className="space-y-2">
                  <h4 className="font-medium text-sm text-slate-700">Detalles</h4>
                  {metricDialog.metric.details.map((detail, i) => (
                    <div key={i} className="flex justify-between items-center py-2 border-b border-slate-100">
                      <span className="text-sm text-slate-600">{detail.label}</span>
                      <span className="font-medium">{detail.value}</span>
                    </div>
                  ))}
                </div>
                
                {/* Tips */}
                <div className="space-y-2">
                  <h4 className="font-medium text-sm text-slate-700">Consejos</h4>
                  <ul className="space-y-1">
                    {metricDialog.metric.tips.map((tip, i) => (
                      <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                        <span className="text-blue-500 mt-1">•</span>
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AnalyticsTab;
